import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  Chip,
  Collapse,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { adjustOpacity } from '../../../theme/utils';
import { createIOSButtonStyles } from '../../../theme/iosButtonStyles';
import { DialogTitleWithClose } from '../../common';
import { StatusChip } from './utils';

/**
 * ExecutionViewDialog Component
 *
 * Dialog for viewing execution details.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onClose - Handler for closing dialog
 * @param {Object|null} props.execution - Selected execution object
 */
function ExecutionViewDialog({ open, onClose, execution }) {
  const theme = useTheme();
  const iosButtonStyles = createIOSButtonStyles(theme);
  const [inputDataExpanded, setInputDataExpanded] = useState(false);
  const [responseExpanded, setResponseExpanded] = useState(false);

  const handleClose = () => {
    setInputDataExpanded(false);
    setResponseExpanded(false);
    onClose();
  };

  if (!execution) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitleWithClose
        title={
          <Box>
            <Typography variant="h6" component="span">
              Execution Details
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              display="block"
              sx={{ mt: 0.5 }}
            >
              {execution.id}
            </Typography>
          </Box>
        }
        onClose={handleClose}
      />
      <DialogContent>
        <Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2">Template</Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 0.5,
                }}
              >
                <Typography variant="body2">
                  {execution.template_name || 'Unnamed template'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontFamily="monospace">
                    {execution.template_id}
                  </Typography>
                  {execution.template_version && (
                    <Chip
                      label={`v${execution.template_version}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2">Status</Typography>
              <Box>
                <StatusChip success={execution.success} />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2">Execution Time</Typography>
              <Typography variant="body2">
                {execution.execution_time?.toFixed(2)}s
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2">Cost</Typography>
              <Typography variant="body2">
                ${execution.cost?.toFixed(4) || '0.0000'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2">Model</Typography>
              <Typography variant="body2">{execution.model_used}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2">Provider</Typography>
              <Typography variant="body2">{execution.provider_used}</Typography>
            </Grid>
            {execution.is_experiment && (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2">Experiment ID</Typography>
                  <Typography variant="body2">
                    {execution.experiment_id}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2">Treatment</Typography>
                  <Typography variant="body2">
                    {execution.treatment_name}
                  </Typography>
                </Grid>
              </>
            )}
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 1 }}
              >
                Input Data
              </Typography>
              <Paper
                sx={{
                  p: 2,
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? adjustOpacity(theme.palette.background.paper, 0.5)
                      : theme.palette.grey[50] ||
                        adjustOpacity(theme.palette.text.primary, 0.05),
                  position: 'relative',
                }}
              >
                <Collapse in={inputDataExpanded} collapsedSize={100}>
                  <pre
                    style={{
                      margin: 0,
                      fontSize: '0.875rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {JSON.stringify(execution.input_data, null, 2)}
                  </pre>
                </Collapse>
                {JSON.stringify(execution.input_data, null, 2).length > 200 && (
                  <Button
                    size="small"
                    onClick={() => setInputDataExpanded(!inputDataExpanded)}
                    endIcon={
                      inputDataExpanded ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )
                    }
                    sx={{
                      mt: 1,
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      color: 'primary.main',
                      minWidth: 'auto',
                      px: 1,
                    }}
                  >
                    {inputDataExpanded ? 'Show less' : 'Show more'}
                  </Button>
                )}
              </Paper>
            </Grid>
            {execution.error_message && (
              <>
                <Grid size={{ xs: 12 }}>
                  <Typography
                    variant="subtitle2"
                    color="error"
                    fontWeight="bold"
                  >
                    {execution.error_code || 'Error'}
                  </Typography>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: theme.palette.error.light,
                      color: theme.palette.error.contrastText,
                      mt: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      style={{ whiteSpace: 'pre-wrap' }}
                    >
                      {execution.error_message}
                    </Typography>
                  </Paper>
                </Grid>
                {execution.validation_errors &&
                  execution.validation_errors.length > 0 && (
                    <Grid size={{ xs: 12 }}>
                      <Typography
                        variant="subtitle2"
                        color="error"
                        fontWeight="bold"
                      >
                        Validation Errors ({execution.validation_errors.length})
                      </Typography>
                      <Paper
                        sx={{
                          p: 2,
                          bgcolor: theme.palette.error.light,
                          color: theme.palette.error.contrastText,
                          mt: 1,
                          maxHeight: 200,
                          overflow: 'auto',
                        }}
                      >
                        <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                          {JSON.stringify(execution.validation_errors, null, 2)}
                        </pre>
                      </Paper>
                    </Grid>
                  )}
              </>
            )}
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 1 }}
              >
                Response
              </Typography>
              <Paper
                sx={{
                  p: 2,
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? adjustOpacity(theme.palette.background.paper, 0.5)
                      : theme.palette.grey[50] ||
                        adjustOpacity(theme.palette.text.primary, 0.05),
                  position: 'relative',
                }}
              >
                <Collapse in={responseExpanded} collapsedSize={100}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.875rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontFamily: 'monospace',
                    }}
                  >
                    {execution.raw_response || 'No response'}
                  </Typography>
                </Collapse>
                {(execution.raw_response || '').length > 200 && (
                  <Button
                    size="small"
                    onClick={() => setResponseExpanded(!responseExpanded)}
                    endIcon={
                      responseExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
                    }
                    sx={{
                      mt: 1,
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      color: 'primary.main',
                      minWidth: 'auto',
                      px: 1,
                    }}
                  >
                    {responseExpanded ? 'Show less' : 'Show more'}
                  </Button>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          sx={{
            ...iosButtonStyles.secondary,
            textTransform: 'none',
            fontFamily:
              "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ExecutionViewDialog;
